/**
 * ⚡ Automatisierungen — the Automation Center workspace (A4, A5b, D9, D10, D11b, S1).
 *
 * A RAIL AND A WORKSPACE. On the left, the automations to choose from (search,
 * category filter, one compact row each). On the right, the chosen one: a
 * header that is true on every tab (name, category, description, status word,
 * the switch) and its tabs — Allgemein, Auslöser, Ausführungen, Protokoll, and
 * for an automation that sends e-mail "E-Mail-Vorlage" as the second (D10).
 * There is still no "+ Neue Automation": it has no endpoint, and a placeholder
 * would be the most prominent thing on the page and the one part that does
 * nothing.
 *
 * THE URL SAYS WHICH AUTOMATION AND WHICH TAB (D9). `?automation=<key>&tab=<tab>`
 * on the existing route; `useAutomationCenterUrl` reads and writes it. Reload
 * keeps both, back and forward follow them, and a link opens exactly that
 * automation on exactly that tab. What the reader DOES is a history entry (a
 * row, a tab); what the page CORRECTS is a replace (see the effect below).
 *
 * THE SELECTION IS DERIVED DURING RENDER, from the URL and the visible list,
 * never synchronised from an effect — an effect would commit one frame of a
 * detail the filter has just hidden, and pay a request to correct it:
 *
 *   - the URL's automation, if it is in the visible list;
 *   - otherwise, on a wide screen, the first visible row;
 *   - otherwise (a phone) nothing, until a tap or a valid deep link.
 *
 * AND SO IS THE TAB, WHICH IS WHAT ENDS THE D8 ASYMMETRY. The URL's tab applies
 * only to the automation the URL names. When the shown automation is a
 * different one — a filter hid the named one, the key was unknown — the tab is
 * Allgemein, in the same render. Until D9 a click reset the tab and a filter did
 * not, so an open Ausführungen tab followed the filter onto the next automation
 * and fetched its history unasked (D8-E4). Now every real change of automation
 * — a click, a filter, a search — lands on Allgemein; only an explicit, valid
 * `tab` in the URL for the automation it names is honoured.
 *
 * THE URL NEVER NAMES AN AUTOMATION THAT IS NOT ON SCREEN. Once the list has
 * arrived, a named key that is unknown or filtered away is replaced by what is
 * shown (or removed, when nothing is). A bare URL that is merely showing the
 * first row is left bare: it names nothing, so it claims nothing. Nor does it
 * name a tab the shown automation does not have: `tab=template` on the move-in
 * address is Allgemein, and the address is corrected (D10).
 *
 * ONE RUN CAN BE OPEN, AND THE URL SAYS WHICH (D11b).
 * `?automation=<key>&tab=executions&run=<id>` opens that run's detail panel
 * over the workspace. The run is honoured only for the automation the URL names
 * and only on its Ausführungen tab; anything else drops it with a replace, and
 * every change of automation or tab drops it with the reader's own push. The
 * detail is read once per run and kept for the page's lifetime; the list never
 * reads a detail.
 *
 * A MOVE-IN REVIEW CAN BE ANSWERED FROM THE OPEN RUN (S1). A run that skipped
 * because the address needs a person's decision reads its review once its
 * detail has arrived (`useMoveInReview`) — no other run, and no list row,
 * costs that request. An answer re-reads the review and the run history it
 * just added a row to, nothing else; "Aktualisieren" forgets the reviews with
 * the runs.
 *
 * THREE TABS COST REQUESTS, EACH ONLY ONCE OPEN. Ausführungen reads the run
 * history, Protokoll the activity timeline — the hooks are gated on the active
 * tab — and E-Mail-Vorlage loads its template inside its own panel, gated on
 * the same signal (`templateActive`). A mount, Allgemein and Auslöser cost
 * nothing beyond the list and the detail. The mount budget is unchanged: two
 * requests on a wide screen, one on a phone, none for a manager.
 *
 * THE WRITE'S ANSWER IS APPLIED HERE BECAUSE ONLY HERE CAN SEE ALL ITS STATES.
 * The PATCH response — a complete detail object — goes to the list row and the
 * detail, and, when the Protokoll of that automation is open, triggers one
 * re-read of it: the switch just wrote the audit row that tab exists to show
 * (Regel 49). Nothing else is re-read. A test send of the template tab
 * (TEST-EMAIL T3) is reported the same way, by key, and costs the same at most
 * one re-read — none while its own Protokoll is not the tab on screen.
 *
 * ADMIN ONLY, AND THE GATE RUNS BEFORE THE REQUESTS. Every endpoint behind this
 * page is `require_roles("admin")`; a manager sees a refusal and costs nothing.
 * The backend remains the actual boundary.
 *
 * ONE BREAKPOINT DECIDES THE SURFACE, A SECOND ONE THE LAYOUT. Below `md`
 * (`useIsMobile`, 767px) the workspace opens in a bottom sheet and nothing is
 * selected until a tap or a deep link. From `md` it is rendered in the page.
 * Rail and workspace stand side by side only from `xl`: the admin sidebar
 * appears at `lg` and takes 288px, which leaves the page 608px at a 1024px
 * viewport — too little for a rail AND a workspace with four tabs. Below `xl`
 * the rail sits above the workspace, both full width. The workspace is one
 * element in every case, mounted once and fetching once.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";

import { useAuth } from "../../contexts/AuthContext";
import { useIsMobile } from "../../hooks/useIsMobile";
import { ResponsiveModal } from "../../components/ui/responsive-modal";
import { Skeleton } from "../../components/ui/skeleton";
import AutomationFilters, {
  ALL_CATEGORIES,
  buildCategoryTabs,
  filterAutomations,
} from "./automations/AutomationFilters";
import AutomationList from "./automations/AutomationList";
import AutomationRunDetailPanel from "./automations/AutomationRunDetailPanel";
import AutomationWorkspace from "./automations/AutomationWorkspace";
import {
  DEFAULT_WORKSPACE_TAB,
  EXECUTIONS_TAB,
  PROTOCOL_TAB,
  TEMPLATE_TAB,
  isWorkspaceTabAvailable,
} from "./automations/automationWorkspaceTabs";
import useAutomationCenterUrl from "./automations/useAutomationCenterUrl";
import useMoveInReview from "./automations/useMoveInReview";
import {
  useAutomationActivity,
  useAutomationDetail,
  useAutomationRunDetail,
  useAutomationRuns,
  useAutomationsList,
} from "./automations/useAutomationsData";

function PageHeader({ actions = null }) {
  return (
    <header className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
      <div className="flex flex-col gap-1">
        <h1 className="m-0 text-[19px] font-semibold text-foreground">
          Automatisierungen
        </h1>
        <p className="m-0 max-w-2xl text-[12.5px] text-muted-foreground">
          Alle automatisierten Prozesse, E-Mails und geplanten Aufgaben auf einen
          Blick.
        </p>
      </div>
      {/* Structural slot for A9's "+ Neue Automation". Empty today and rendering
          nothing — a placeholder would be the dead CTA this page refuses. */}
      {actions}
    </header>
  );
}

function ListSkeleton() {
  return (
    <div className="rounded-[14px] border border-border bg-card p-4">
      <Skeleton className="h-4 w-48" />
      <Skeleton className="mt-4 h-3 w-full" />
      <Skeleton className="mt-2 h-3 w-5/6" />
      <Skeleton className="mt-2 h-3 w-2/3" />
    </div>
  );
}

export default function AdminAutomationsPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const isMobile = useIsMobile();

  const [category, setCategory] = useState(ALL_CATEGORIES);
  const [query, setQuery] = useState("");
  const url = useAutomationCenterUrl();

  const list = useAutomationsList({ active: isAdmin });

  const visible = useMemo(
    () => filterAutomations(list.items, category, query),
    [list.items, category, query]
  );
  const tabs = useMemo(() => buildCategoryTabs(list.items), [list.items]);

  // What is shown — see the module docstring for the three cases.
  const requestedIsVisible =
    url.requestedKey !== null &&
    visible.some((item) => item.key === url.requestedKey);
  let selectedKey = requestedIsVisible ? url.requestedKey : null;
  if (!selectedKey && !isMobile) {
    selectedKey = visible.length ? visible[0].key : null;
  }

  // The URL's tab belongs to the automation the URL names; any other shown
  // automation opens on Allgemein — in this render, before any request.
  const urlTab =
    url.requestedKey === null || selectedKey === url.requestedKey
      ? url.requestedTab
      : DEFAULT_WORKSPACE_TAB;
  // And it has to be a tab this automation HAS (D10): `tab=template` on the
  // move-in address is Allgemein, decided from the list row the page already
  // holds — so the template is never asked for on its behalf.
  const selectedItem = selectedKey
    ? visible.find((item) => item.key === selectedKey) || null
    : null;
  const urlTabAvailable =
    !selectedItem || isWorkspaceTabAvailable(urlTab, selectedItem);
  const activeTab = urlTabAvailable ? urlTab : DEFAULT_WORKSPACE_TAB;

  const detail = useAutomationDetail({
    active: isAdmin && !!selectedKey,
    automationKey: selectedKey,
  });

  const runs = useAutomationRuns({
    // The tab has to be OPEN, not merely present: this is what keeps a browsed
    // list from costing one run history per automation.
    active: isAdmin && !!selectedKey && activeTab === EXECUTIONS_TAB,
    automationKey: selectedKey,
  });

  const activity = useAutomationActivity({
    active: isAdmin && !!selectedKey && activeTab === PROTOCOL_TAB,
    automationKey: selectedKey,
  });

  // A run is open only for the automation the URL NAMES and only on its
  // Ausführungen tab (D11b). A `run` anywhere else is not shown and not read —
  // the effect below removes it from the address.
  const runAllowed =
    isAdmin &&
    selectedKey !== null &&
    url.requestedKey === selectedKey &&
    activeTab === EXECUTIONS_TAB;
  const openRunId = runAllowed ? url.requestedRun : null;
  const runDetail = useAutomationRunDetail({
    active: !!openRunId,
    automationKey: selectedKey,
    runId: openRunId,
  });
  // The move-in review behind the open run (S1) — asked only for a review
  // skip, once its detail is here. An answer adds a run to the history, so the
  // history of the automation it was GIVEN FOR is read again (the reader may
  // have switched away while it was on its way); the run that was open is
  // history and stays as it is.
  const runReview = useMoveInReview({
    automationKey: selectedKey,
    runId: openRunId,
    run: runDetail.run,
    onResolved: (_result, { automationKey }) => runs.refresh(automationKey),
  });
  // On a phone the run opens ABOVE the workspace sheet, and two modal layers
  // stack in the order they mount: the one mounted last is on top and hides
  // the other from assistive technology. A deep link opens both at once, so
  // the run waits until the sheet shows this automation — its detail has
  // arrived. The run's own read does not wait (it started above).
  const runPanelOpen =
    !!openRunId && (!isMobile || (!!detail.detail && detail.detail.key === selectedKey));

  /**
   * Bring the address in line with what is on screen — a replace, never a push.
   *
   * Only once the list has arrived: before that, every key looks unknown.
   * Three cases. A named automation that is not the one shown (unknown key,
   * filtered away, or nothing shown on a phone) is replaced by the shown one on
   * Allgemein, or removed. A named automation that is shown, with a tab it does
   * not have (D10: `tab=template` on the move-in address), is corrected to
   * Allgemein. A bare URL with an explicit tab is bound to the automation it is
   * showing — on that tab if the automation has it, else on Allgemein — so that
   * a later filter change is a change of automation like any other and resets
   * the tab.
   */
  const {
    requestedKey,
    requestedTab,
    requestedRun,
    hasExplicitTab,
    replaceAutomation,
    clearRun,
  } = url;
  const listLoaded = list.loaded;
  useEffect(() => {
    if (!isAdmin || !listLoaded) return;
    if (requestedKey !== null && requestedKey !== selectedKey) {
      replaceAutomation(selectedKey);
    } else if (requestedKey !== null && hasExplicitTab && !urlTabAvailable) {
      replaceAutomation(selectedKey);
    } else if (requestedKey === null && hasExplicitTab && selectedKey) {
      replaceAutomation(
        selectedKey,
        urlTabAvailable ? requestedTab : DEFAULT_WORKSPACE_TAB
      );
    } else if (requestedRun !== null && !runAllowed) {
      // D11b: a run named without its automation, or on another tab.
      clearRun();
    }
  }, [
    isAdmin,
    listLoaded,
    requestedKey,
    requestedTab,
    requestedRun,
    hasExplicitTab,
    selectedKey,
    urlTabAvailable,
    runAllowed,
    replaceAutomation,
    clearRun,
  ]);

  /** A click on a row: a different automation opens on Allgemein. */
  const handleSelect = (key) => {
    if (key === selectedKey) return;
    url.openAutomation(key);
  };

  const handleTabChange = (tab) => {
    if (!selectedKey || tab === activeTab) return;
    url.openTab(selectedKey, tab);
  };

  /** A row of Ausführungen: that run opens. The run already open costs nothing. */
  const handleOpenRun = (run) => {
    if (!selectedKey || !run?.id || run.id === openRunId) return;
    url.openRun(selectedKey, run.id);
  };

  /**
   * "Aktualisieren": forget this automation's pages AND the runs already read,
   * then read the page on screen again. A run that was `running` an hour ago
   * may have finished.
   */
  const handleRefreshRuns = () => {
    runs.refresh();
    runDetail.forgetAutomation(selectedKey);
    runReview.forgetAutomation(selectedKey);
  };

  /**
   * Fold one PATCH response into every state that shows it, and re-read only
   * the one view the write itself changed: an open Protokoll of that
   * automation. Each applier decides for itself whether the answer still
   * belongs to it — the list row always does, the detail and the timeline only
   * while they are still showing that automation.
   */
  const handleAutomationUpdated = (updated) => {
    list.applyAutomation(updated);
    detail.applyAutomation(updated);
    activity.refreshIfShowing(updated?.key);
  };

  /** A test send the server may have recorded: its Protokoll, if open, again. */
  const handleTestEmailAttempted = (key) => {
    activity.refreshIfShowing(key);
  };

  const workspace = (
    <AutomationWorkspace
      isMobile={isMobile}
      detail={detail.detail}
      loading={detail.loading}
      error={detail.error}
      onRetry={detail.reload}
      activeTab={activeTab}
      onTabChange={handleTabChange}
      runs={runs}
      activity={activity}
      templateActive={activeTab === TEMPLATE_TAB}
      onAutomationUpdated={handleAutomationUpdated}
      onTestEmailAttempted={handleTestEmailAttempted}
      openRunId={openRunId}
      onOpenRun={handleOpenRun}
      onRefreshRuns={handleRefreshRuns}
    />
  );

  /**
   * What the sheet showed last, for the moment it slides away.
   *
   * Closing the sheet — by its own gesture or by the back button — removes the
   * selection at once, because the address must stop naming it. The drawer,
   * though, animates out for a moment, and with nothing selected the workspace
   * would turn into "Automatisierung auswählen." while the reader watches it
   * go. So the sheet keeps the last workspace it showed (the usePrevious
   * pattern: written after commit, read in render). Nothing is fetched for it:
   * it is the element as it was, not a new request.
   */
  const lastSheetWorkspace = useRef(null);
  useEffect(() => {
    if (isMobile && selectedKey) lastSheetWorkspace.current = workspace;
  });

  if (!isAdmin) {
    return (
      <div className="flex flex-col gap-4 p-4 md:p-6">
        <PageHeader />
        <p
          role="alert"
          className="m-0 rounded-[12px] border border-border bg-card px-4 py-6 text-[12.5px] text-muted-foreground"
        >
          Für die Automatisierungen benötigst du Administratorrechte.
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 p-4 md:p-6">
      <PageHeader />

      {list.loading || (!list.loaded && !list.error) ? (
        <ListSkeleton />
      ) : list.error ? (
        <div className="flex flex-col items-start gap-2 rounded-[12px] border border-border bg-card px-4 py-6">
          <p role="alert" className="m-0 text-[12.5px] text-destructive">
            {list.error}
          </p>
          <button
            type="button"
            onClick={list.reload}
            className="rounded-[9px] border border-border bg-card px-3 py-1.5 text-[12px] font-semibold text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring max-md:min-h-9"
          >
            Erneut versuchen
          </button>
        </div>
      ) : !list.items.length ? (
        <p className="m-0 rounded-[12px] border border-border bg-card px-4 py-6 text-center text-[12.5px] text-muted-foreground">
          Noch keine Automatisierungen verfügbar.
        </p>
      ) : (
        <>
          <div className="grid grid-cols-1 gap-4 xl:grid-cols-[300px_minmax(0,1fr)] xl:items-start">
            {/* The rail. Its filters govern the list and nothing else, so they
                live inside it rather than above both columns. */}
            <div className="flex min-w-0 flex-col gap-3 rounded-[14px] border border-border bg-card p-3">
              <AutomationFilters
                tabs={tabs}
                category={category}
                onCategoryChange={setCategory}
                query={query}
                onQueryChange={setQuery}
              />
              <AutomationList
                items={visible}
                selectedKey={selectedKey}
                onSelect={handleSelect}
              />
            </div>

            {/* The workspace — NOT rendered at all on a phone, where the same
                element lives in the sheet below. Hiding it with CSS would mount
                it twice and issue its requests twice. */}
            {isMobile ? null : (
              <div className="min-w-0 rounded-[14px] border border-border bg-card p-4 md:p-5">
                {workspace}
              </div>
            )}
          </div>

          {/* A CONSTANT TITLE: the workspace header two lines below carries the
              automation's name. Closing the sheet removes the selection from
              the URL (a replace), so a reload does not reopen it; while it
              slides away it keeps showing what it showed (see above).
              BOUNDED HEIGHT (D10): the drawer itself has none, and the
              template tab is taller than a phone — without a bound the sheet
              outgrew the screen and its top (status, actions) was out of
              reach. Bounded, its body scrolls.
              A DESCRIPTION FOR SCREEN READERS (Final QA): a dialog without
              one is announced by its title alone, and Radix warns about it.
              The header inside already shows name and description, so this
              one is read, not shown. */}
          <ResponsiveModal
            open={isMobile && !!selectedKey}
            onOpenChange={(open) => {
              if (!open) replaceAutomation(null);
            }}
            title="Automatisierung"
            description={
              <span className="sr-only">
                Status, Einstellungen, Ausführungen und Protokoll der
                gewählten Automatisierung.
              </span>
            }
            contentClassName="max-h-[90dvh]"
          >
            {selectedKey ? workspace : lastSheetWorkspace.current}
          </ResponsiveModal>

          {/* One run, opened (D11b). Over the workspace on every width — a side
              panel from `md`, a bottom sheet above the workspace sheet below
              it. Closing is the reader's step back out: a push. */}
          <AutomationRunDetailPanel
            open={runPanelOpen}
            isMobile={isMobile}
            runId={openRunId}
            run={runDetail.run}
            loading={runDetail.loading}
            error={runDetail.error}
            notFound={runDetail.notFound}
            actionType={selectedItem?.action_type}
            review={runReview}
            onRetry={runDetail.reload}
            onClose={url.closeRun}
          />
        </>
      )}
    </div>
  );
}
